import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { ApiService } from '../../lib/api.service';
import { LogService } from '../../lib/log.service';
import { ModalService } from '../../lib/modal.service';
import { Organization } from '../../models/organization';
import { User } from '../../models/user';
import { UserService } from '../../lib/user.service';
import { TranslateService } from '@ngx-translate/core';
import { config } from '../../../environments/all';
import { MultiAutoItem } from '../../models/multiautoitem';
import { AnalyticEvent } from '../../lib/analytic-event';

@Component({
    selector: 'app-user-invite',
    templateUrl: './user-invite.component.html',
    styleUrls: ['./user-invite.component.scss'],
    standalone: false,
})
export class UserInviteComponent implements OnInit {
    userInviteForm: FormGroup;
    organizationDataForm: FormGroup;
    isLoading = true;
    currentUser: User;
    organizations: Organization[];
    multiSelOrgs: Array<MultiAutoItem> = [];
    organizationIds = [];
    roles: Array<any> = [];
    timezones: any;
    userId: number;
    existingUser: User;
    isEditInvite: boolean = false;

    constructor(
        private formBuilder: FormBuilder,
        private api: ApiService,
        private log: LogService,
        private modalService: ModalService,
        private translate: TranslateService,
        private userService: UserService,
        private router: Router,
        private route: ActivatedRoute,
        private location: Location
    ) {
        this.currentUser = this.userService.getUser();
        this.timezones = config.timezones;

        if (this.currentUser.userType === 'superadmin') {
            this.roles = [
                { id: 6, name: 'Relationship Manager' },
                { id: 7, name: 'Admin' },
            ];
        } else if (this.currentUser.userType === 'relationshipmanager') {
            this.roles = [{ id: 6, name: 'Relationship Manager' }];
        }
    }

    ngOnInit() {
        const paramId = this.route.snapshot.paramMap.get('id');
        if (paramId) {
            this.userId = +paramId;
            this.isEditInvite = true;
        }

        this.initForms();
        this.loadOrganizations();

        if (this.isEditInvite) {
            this.loadUser();
        }
    }

    initForms() {
        this.userInviteForm = this.formBuilder.group({
            fullName: ['', [Validators.required]],
            email: ['', [Validators.required, Validators.pattern(/^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,4},?)+$/)]],
            notificationOn: [true],
            timezone: [''],
            roleId: [6],
            lockedRole: [false],
            message: [''],
        });

        this.organizationDataForm = this.formBuilder.group({
            orgId: [[], [Validators.required]],
        });
    }

    loadOrganizations() {
        this.api.get('admin/organizations?parent=1').subscribe(
            (results: any) => {
                this.organizations = Organization.initializeArray(results.data);
                this.multiSelOrgs = [];
                this.organizations.forEach(org => {
                    this.multiSelOrgs.push(new MultiAutoItem(org.id, org.name));
                });
                if (!this.isEditInvite) {
                    this.isLoading = false;
                }
            },
            (error: any) => {
                this.log.error('Error loading organizations. ' + error.message);
                this.isLoading = false;
            }
        );
    }

    loadUser() {
        this.api.get('users/' + this.userId, {}).subscribe(
            (result: any) => {
                this.existingUser = new User(result.data);
                this.userInviteForm.patchValue({
                    fullName: this.existingUser.fullName,
                    email: this.existingUser.email,
                });
                this.isLoading = false;
            },
            (error: any) => {
                this.log.error('Error fetching user. ' + error.message);
                this.isLoading = false;
            }
        );
    }

    get f() {
        return this.userInviteForm.controls;
    }

    onSave() {
        if (this.userInviteForm.invalid || this.organizationDataForm.invalid) {
            this.userInviteForm.markAllAsTouched();
            this.organizationDataForm.markAllAsTouched();
            return;
        }

        const roleId = this.userInviteForm.value.roleId;
        const apiUrl = roleId === 6 ? 'invitations/rminvite' : 'invitations/admininvite';

        this.api
            .post(apiUrl, {
                Email: this.userInviteForm.value.email,
                Message: this.userInviteForm.value.message,
                OrgID: this.organizationDataForm.value.orgId,
                NotificationOn: this.userInviteForm.value.notificationOn,
                lockedRole: this.userInviteForm.value.lockedRole,
                Timezone: this.userInviteForm.value.timezone,
            })
            .subscribe(
                (result: any) => {
                    this.modalService.showAlert('Success', result.message);
                    this.log.event(AnalyticEvent.event.userInvitationSent);
                    this.router.navigate(['/admin/user-listing']);
                },
                (error: any) => {
                    this.modalService.showAlert('Error', error.message);
                }
            );
    }

    onCancel() {
        this.location.back();
    }

    onRoleChange() {
        const roleId = this.userInviteForm.value.roleId;
        if (roleId === 6) {
            this.organizationDataForm.get('orgId').setValidators([Validators.required]);
            this.organizationDataForm.get('orgId').updateValueAndValidity();
        } else {
            this.organizationDataForm.get('orgId').clearValidators();
            this.organizationDataForm.get('orgId').updateValueAndValidity();
        }
    }

    onSelectOrgs(items: MultiAutoItem[]) {
        this.organizationIds = items.map(item => item.id);
        this.organizationDataForm.patchValue({
            orgId: this.organizationIds,
        });
    }
}
